import React from 'react';
import styled from 'styled-components';
import { ink, inset, space, typeRole } from '@repo/design-tokens';
import { Card, Kbd } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';

/**
 * The camera view's keys (FR12): card 22's list, plus the three the stage adds, plus moving a card
 * by its grip (`observers-page-motion`, FR4), plus drawing and stepping through regions with no mode
 * (`observer-unified-draw-edit`, R6). Over the picture Tab steps through regions rather than moving
 * focus, so its row says how focus gets out (Rob, 2026-10-28).
 */
export const SHORTCUTS: ReadonlyArray<{ keys: string[]; label: string }> = [
  { keys: ['Drag', '←', '↑', '→', '↓'], label: 'Pan' },
  { keys: ['Scroll', '+', '−'], label: 'Zoom' },
  { keys: ['R'], label: 'Recentre' },
  { keys: ['Esc'], label: 'Cancel or deselect' },
  { keys: ['Shift', 'click'], label: 'Start a new region' },
  {
    keys: ['Tab', 'click'],
    label:
      'Select the region underneath (Shift for the other way). Move the pointer off the picture to Tab between controls',
  },
  { keys: ['Esc'], label: 'Also cancels Add Observer' },
  { keys: ['Ctrl', 'Z'], label: 'Undo' },
  { keys: ['Delete'], label: 'Delete observer' },
  { keys: ['P'], label: 'Plan' },
  { keys: ['N'], label: 'Next machine to review (Shift for previous)' },
  { keys: ['A', 'K', 'E', 'L'], label: 'Use · Keep mine · Adjust · Wrong machine' },
  { keys: ['O', 'Enter'], label: 'Next heat outline (Shift for previous) · add it as a region' },
  { keys: ['?'], label: 'Show or hide this card' },
  {
    keys: ['←', '↑', '→', '↓', 'Home'],
    label: 'Arrow keys on a grip — move the card · Home — put it back',
  },
];

/**
 * The shortcut card above the dock, closed by default (FR12). An app composite on `Card` and `Kbd` —
 * staging's `Tooltip` has no card shape, and this is not a tooltip. The list stays in the DOM while
 * closed, visually hidden, because the canvas is described by it.
 */
export const ShortcutCard: React.FC<{ open: boolean; id: string }> = ({ open, id }) => (
  <Root surface="on-scene" data-control="" style={open ? undefined : visuallyHidden}>
    <List id={id} aria-label="Keyboard shortcuts">
      {SHORTCUTS.map(s => (
        <li key={s.label}>
          <span className="keys">
            {s.keys.map(k => (
              <Kbd key={k} keys={k} />
            ))}
          </span>
          <span className="label">{s.label}</span>
        </li>
      ))}
    </List>
  </Root>
);

const Root = styled(Card)`
  pointer-events: auto;
`;

const List = styled.ul`
  display: grid;
  gap: ${space[1]};
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[3]};
  }
  .keys {
    display: inline-flex;
    gap: ${inset[1]};
  }
  .label {
    ${typeRole.caption1};
    color: ${ink[2]};
  }
`;
